/* La paleta de los gráficos, sacada del tema que esté puesto (UAT).
 *
 * ── POR QUÉ HACE FALTA ──────────────────────────────────────────────────────
 *
 * Los gráficos no leen CSS: recharts y el calendario reciben los colores como
 * texto (ver `shared/tema.js`). Por eso con Industrial puesto todo el hub era
 * amarillo y negro y los gráficos de Presidencia seguían rojos y grafito, con
 * su tarjeta blanca de siempre.
 *
 * Cada tema trae dos colores pensados para esto (`--chart1`, `--chart2`) y el
 * resto sale de lo que ya define: la tinta para los ejes, el filete para la
 * grilla, la superficie para el fondo.
 *
 * ── POR QUÉ SE DIBUJA PARA LEER UN COLOR ────────────────────────────────────
 *
 * Varios temas escriben sus colores como `color-mix(…)`. El calendario
 * necesita el rojo, el verde y el azul por separado (pinta con transparencia)
 * y una escala de cinco pasos entre el fondo y el color: con una fórmula en
 * texto no se puede hacer ninguna de las dos cosas. Se pinta un punto con ese
 * color y se lee qué quedó — lo resuelve el navegador, que es el único que
 * sabe de verdad cuánto da.
 *
 * Sin tema (producción), o donde no hay con qué dibujar, devuelve `null` y
 * quien llama se queda con la paleta de siempre.
 */

let lienzo = null;

export function resolverColor(css) {
  if (!css || typeof document === "undefined") return null;
  try {
    if (!lienzo) {
      const c = document.createElement("canvas");
      c.width = 1;
      c.height = 1;
      lienzo = c.getContext("2d", { willReadFrequently: true });
    }
    if (!lienzo) return null;
    // Un color que el lienzo no entiende deja el anterior puesto. Probando
    // con dos anteriores distintos se nota: si el resultado cambia, no era
    // el color pedido.
    const leer = (previo) => {
      lienzo.fillStyle = previo;
      lienzo.fillStyle = css;
      lienzo.clearRect(0, 0, 1, 1);
      lienzo.fillRect(0, 0, 1, 1);
      return Array.from(lienzo.getImageData(0, 0, 1, 1).data);
    };
    const a = leer("#000000");
    const b = leer("#ffffff");
    if (a.join() !== b.join() || a[3] < 250) return null;
    return a.slice(0, 3);
  } catch {
    return null;
  }
}

const hex = ([r, g, b]) =>
  "#" + [r, g, b].map((n) => Math.round(n).toString(16).padStart(2, "0")).join("");

const mezclar = (a, b, t) => a.map((n, i) => n + (b[i] - n) * t);

const lejos = (a, b) =>
  Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]) > 90;

/* `base` es la paleta de siempre para ese modo (clara u oscura): de ahí salen
 * lo que el tema no define —los pisos del calendario— y los colores que hagan
 * falta para completar las ocho porciones de la torta. */
export function paletaDelTema(base) {
  if (typeof document === "undefined") return null;
  const raiz = document.documentElement;
  if (!raiz.getAttribute("data-tema")) return null;

  const estilo = window.getComputedStyle(raiz);
  const color = (n) => resolverColor(estilo.getPropertyValue("--" + n).trim());

  const c1 = color("chart1");
  const c2 = color("chart2");
  const panel = color("surf");
  const tinta = color("ink");
  if (!c1 || !c2 || !panel || !tinta) return null;

  const tinta2 = color("ink2") || tinta;
  const tinta3 = color("ink3") || tinta2;
  const filete = color("hair") || mezclar(panel, tinta, 0.18);

  // Las ocho de la torta: las dos del tema, sus cuatro de estado, y lo que
  // falte de la paleta de siempre. Dos que se parecen no entran las dos: en
  // Industrial el aviso es el mismo amarillo que la segunda serie.
  const serie = [];
  const sumar = (c) => {
    if (c && serie.length < 8 && serie.every((s) => lejos(s, c))) serie.push(c);
  };
  [c1, c2, color("info"), color("ok"), color("crit"), color("warn")].forEach(sumar);
  base.serie.map(resolverColor).forEach(sumar);
  while (serie.length < 8) serie.push(tinta3);

  return {
    ...base,
    ida: hex(c1),
    vuelta: hex(c2),
    serie: serie.map(hex),
    otros: hex(tinta3),
    tinta: hex(tinta2),
    grilla: hex(filete),
    panel: hex(panel),
    borde: hex(filete),
    celda: c1.map(Math.round).join(","),
    // El calendario del año: del casillero vacío al color pleno, en cinco.
    escala: [
      hex(mezclar(panel, tinta, 0.09)),
      hex(mezclar(panel, c1, 0.3)),
      hex(mezclar(panel, c1, 0.55)),
      hex(mezclar(panel, c1, 0.8)),
      hex(c1),
    ],
  };
}
